import { UUID } from 'node:crypto';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { withCustomRetry } from '@shared/utils/with-custom-retry';
import { BehaviorSubject, map, Observable, of, shareReplay, Subject, tap } from 'rxjs';
import { CreateProductionNoteSubscription } from '../../graphql/subscriptions/on-create-production-note';
import { DeleteProductionNoteSubscription } from '../../graphql/subscriptions/on-delete-production-note';
import { UpdateProductionNoteSubscription } from '../../graphql/subscriptions/on-update-production-note';
import { UserService } from './user.service';

export interface IProductionNote {
	id: UUID;
	createdAt: string;
	sequenceNumber: number;
	note: string;
	createdBy: string;
	itemAlias: string;
	batchId: UUID;
}

export interface IProductionNotePostRequest {
	sequenceNumber: number;
	note: string;
	createdBy: string;
	itemAlias: string;
	batchId: UUID;
}

export interface IProductionNoteResponse {
	id: UUID;
	createdAt: string;
	sequenceNumber: number;
	note: string;
	createdBy: string;
	itemAlias: string;
	batchId: UUID;
}

export interface IProductionNoteEvent {
	type: 'create' | 'update' | 'delete';
	note: IProductionNote;
}

@Injectable({
	providedIn: 'root',
})
export class ProductionNoteService {
	private noteCache = new Map<string, IProductionNote>();
	private batchIndex = new Map<string, Set<string>>();
	private orderIndex = new Set<string>();
	private orderPositionIndex = new Map<string, Set<string>>();
	private inFlightRequests = new Map<string, Observable<IProductionNote[]>>();
	private notesSubject$ = new BehaviorSubject<Map<string, IProductionNote>>(this.noteCache);
	private noteEventsSubject$ = new Subject<IProductionNoteEvent>();
	public notes$ = this.notesSubject$.asObservable();
	public noteEvents$ = this.noteEventsSubject$.asObservable();

	constructor(
		private http: HttpClient,
		private userService: UserService,
		onProductionNoteCreated: CreateProductionNoteSubscription,
		onProductionNoteDeleted: DeleteProductionNoteSubscription,
		onProductionNoteUpdated: UpdateProductionNoteSubscription,
	) {
		onProductionNoteCreated.subscribe$().subscribe(({ onCreateProductionNote }) => {
			this.addNoteToExistingIndexes(onCreateProductionNote as IProductionNote);
		});

		onProductionNoteUpdated.subscribe$().subscribe(({ onUpdateProductionNote }) => {
			this.addNoteToExistingIndexes(onUpdateProductionNote as IProductionNote);
		});

		onProductionNoteDeleted.subscribe$().subscribe(({ onDeleteProductionNote }) => {
			this.removeNoteFromCache(onDeleteProductionNote.id);
		});

		this.userService.accountChanged$.subscribe(() => this.resetCache());
	}

	private resetCache(): void {
		this.noteCache.clear();
		this.batchIndex.clear();
		this.orderIndex.clear();
		this.orderPositionIndex.clear();
		this.inFlightRequests.clear();
		this.notesSubject$.next(this.noteCache);
	}

	getCacheKey(alias: string): string {
		const parts = alias.split(':');
		return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : alias;
	}

	getProductionNotesByAlias(alias: string): Observable<IProductionNote[]> {
		const cacheKey = this.getCacheKey(alias);

		if (this.inFlightRequests.has(cacheKey)) {
			return this.inFlightRequests.get(cacheKey);
		}

		const [order, position] = cacheKey.split(':');

		if (position && this.orderIndex.has(order)) {
			const filtered = this.getNotesForOrder(order).filter((n) => this.getCacheKey(n.itemAlias) === cacheKey);
			const obs$ = of(filtered).pipe(shareReplay(1));
			this.inFlightRequests.set(cacheKey, obs$);
			return obs$;
		}

		if (this.orderPositionIndex.has(cacheKey)) {
			const obs$ = of(this.getNotesByIds(this.orderPositionIndex.get(cacheKey))).pipe(shareReplay(1));
			this.inFlightRequests.set(cacheKey, obs$);
			return obs$;
		}

		const url = `${environment.awsEnvUri}/mes/productionnotes?order=${order}${position ? `&position=${position}` : ''}`;

		const request$ = this.http
			.get<{ status: number; result: IProductionNote[] }>(url, {
				headers: this.userService.getHeaders(),
			})
			.pipe(
				withCustomRetry(),
				map((response) => this.mapResponseToNotes(response.result)),
				tap((notes) => this.storeNotesForAlias(notes, cacheKey, !position)),
				shareReplay(1),
			);

		this.inFlightRequests.set(cacheKey, request$);
		return request$;
	}

	getProductionNotesByBatchId(batchId: string): Observable<IProductionNote[]> {
		if (this.inFlightRequests.has(batchId)) {
			return this.inFlightRequests.get(batchId);
		}

		if (this.batchIndex.has(batchId)) {
			const obs$ = of(this.getNotesByIds(this.batchIndex.get(batchId))).pipe(shareReplay(1));
			this.inFlightRequests.set(batchId, obs$);
			return obs$;
		}

		const url = `${environment.awsEnvUri}/mes/productionnotes?batchId=${batchId}`;

		const request$ = this.http
			.get<{ status: number; result: IProductionNote[] }>(url, {
				headers: this.userService.getHeaders(),
			})
			.pipe(
				withCustomRetry(),
				map((response) => this.mapResponseToNotes(response.result)),
				tap((notes) => this.storeNotesForBatch(notes, batchId)),
				shareReplay(1),
			);

		this.inFlightRequests.set(batchId, request$);
		return request$;
	}

	create(note: IProductionNotePostRequest): Observable<IProductionNote> {
		return this.http.post<{ status: number; result: IProductionNote }>(`${environment.awsEnvUri}/mes/productionnotes`, note, { headers: this.userService.getHeaders() }).pipe(
			map((response) => response.result),
			tap((createdNote) => {
				// Add note directly to noteCache without index updates
				const noteId = createdNote.id as string;
				this.noteCache.set(noteId, createdNote);
				this.noteEventsSubject$.next({ type: 'create', note: createdNote });
				this.notesSubject$.next(this.noteCache);
			}),
		);
	}

	private clearNotesFromIndex(index: Map<string, Set<string>>, key: string): void {
		const noteIds = index.get(key);
		if (noteIds) {
			for (const id of noteIds) this.noteCache.delete(id);
		}
	}

	private storeNotesForAlias(notes: IProductionNote[], aliasKey: string, isOrderLevel: boolean): void {
		if (isOrderLevel) {
			this.orderIndex.add(aliasKey);
		}

		const notesByAlias = new Map<string, IProductionNote[]>();
		for (const note of notes) {
			const key = this.getCacheKey(note.itemAlias);
			if (!notesByAlias.has(key)) notesByAlias.set(key, []);
			notesByAlias.get(key).push(note);
		}

		for (const [key, aliasNotes] of notesByAlias) {
			this.clearNotesFromIndex(this.orderPositionIndex, key);
			this.orderPositionIndex.set(key, this.addNotesToCache(aliasNotes));
		}
	}

	private getNotesForOrder(order: string): IProductionNote[] {
		const notes: IProductionNote[] = [];
		for (const [key, ids] of this.orderPositionIndex) {
			if (key.startsWith(`${order}:`)) {
				notes.push(...this.getNotesByIds(ids));
			}
		}
		return notes;
	}

	private storeNotesForBatch(notes: IProductionNote[], batchId: string): void {
		this.clearNotesFromIndex(this.batchIndex, batchId);

		const notesByAlias = new Map<string, IProductionNote[]>();
		for (const note of notes) {
			const aliasKey = this.getCacheKey(note.itemAlias);
			if (!notesByAlias.has(aliasKey)) notesByAlias.set(aliasKey, []);
			notesByAlias.get(aliasKey).push(note);
		}

		for (const [aliasKey, aliasNotes] of notesByAlias) {
			this.clearNotesFromIndex(this.orderPositionIndex, aliasKey);
			this.orderPositionIndex.set(aliasKey, new Set(aliasNotes.map((n) => n.id as string)));
		}

		this.batchIndex.set(batchId, this.addNotesToCache(notes));
	}

	private addNotesToCache(notes: IProductionNote[]): Set<string> {
		const noteIds = new Set<string>();
		for (const note of notes) {
			const noteId = note.id as string;
			this.noteCache.set(noteId, note);
			noteIds.add(noteId);
		}
		this.notesSubject$.next(this.noteCache);
		return noteIds;
	}

	removeNoteFromCache(noteId: string): void {
		const note = this.noteCache.get(noteId);
		if (!note) return;

		this.noteCache.delete(noteId);

		const aliasKey = this.getCacheKey(note.itemAlias);
		const [order] = aliasKey.split(':');

		this.inFlightRequests.delete(aliasKey);
		this.inFlightRequests.delete(order);

		this.orderPositionIndex.get(aliasKey)?.delete(noteId);
		if (this.orderPositionIndex.get(aliasKey)?.size === 0) this.orderPositionIndex.delete(aliasKey);

		const batchId = note.batchId as string;
		this.inFlightRequests.delete(batchId);

		this.batchIndex.get(batchId)?.delete(noteId);
		if (this.batchIndex.get(batchId)?.size === 0) this.batchIndex.delete(batchId);

		this.noteEventsSubject$.next({ type: 'delete', note });
		this.notesSubject$.next(this.noteCache);
	}

	private addNoteToExistingIndexes(note: IProductionNote): void {
		const noteId = note.id as string;
		this.noteCache.set(noteId, note);

		const aliasKey = this.getCacheKey(note.itemAlias);
		const [order] = aliasKey.split(':');

		this.inFlightRequests.delete(aliasKey);
		this.inFlightRequests.delete(order);

		if (this.orderPositionIndex.has(aliasKey)) {
			this.orderPositionIndex.get(aliasKey).add(noteId);
		} else if (this.orderIndex.has(order)) {
			this.orderPositionIndex.set(aliasKey, new Set([noteId]));
		}

		const batchId = note.batchId as string;
		this.inFlightRequests.delete(batchId);
		this.batchIndex.get(batchId)?.add(noteId);

		this.noteEventsSubject$.next({ type: 'create', note });
		this.notesSubject$.next(this.noteCache);
	}

	private getNotesByIds(ids: Set<string>): IProductionNote[] {
		return [...ids].map((id) => this.noteCache.get(id)).filter(Boolean);
	}

	private mapResponseToNotes(response: IProductionNoteResponse[]): IProductionNote[] {
		return response.map((item) => ({
			id: item.id,
			createdAt: item.createdAt,
			createdBy: item.createdBy,
			itemAlias: item.itemAlias,
			note: item.note,
			sequenceNumber: item.sequenceNumber,
			batchId: item.batchId,
		}));
	}
}
